// Validation for the admin "Enter Final Score" inputs in GameManagement.
//
// Football scores are non-negative integers. The MUI TextField with
// type="number" alone is not sufficient — browsers still accept "-", "e",
// "+", scientific notation, decimals, and pasted text. These helpers
// constrain input so invalid data never reaches Firestore.

/**
 * Strip any characters that aren't digits 0-9 from `raw` and remove leading
 * zeros (preserving a single "0"). Returns a clean string that can be
 * displayed in the input as-is.
 *
 * Examples:
 *   "-3"     -> "3"
 *   "21.5"   -> "215"
 *   "1e5"    -> "15"
 *   " 21 "   -> "21"
 *   "abc"    -> ""
 *   "007"    -> "7"
 *   "0"      -> "0"
 *   ""       -> ""
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeScoreInput(raw) {
  if (raw === null || raw === undefined) return '';
  const digitsOnly = String(raw).replace(/\D/g, '');
  if (digitsOnly === '') return '';
  const trimmed = digitsOnly.replace(/^0+/, '');
  return trimmed === '' ? '0' : trimmed;
}

/**
 * Returns true iff `raw` is a non-empty string representing a non-negative
 * integer with no extraneous characters. Used to gate the Save button on
 * the score-entry modal.
 *
 * Examples:
 *   "0"   -> true
 *   "21"  -> true
 *   ""    -> false   (must enter a score)
 *   "-3"  -> false   (negative)
 *   "2.5" -> false   (not integer)
 *   "1e5" -> false   (notation)
 *   "abc" -> false   (non-numeric)
 *
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isValidScore(raw) {
  if (typeof raw !== 'string' || raw.length === 0) return false;
  return /^(0|[1-9]\d*)$/.test(raw);
}

/**
 * True when a Pick the Score prediction has both scores filled in and equal.
 * A tie names no winner, so it can never earn points (see functions/lib/
 * scoring.js); players have to pick a winner. Mirrored server-side in
 * submitPicks.
 *
 * @param {{ awayScore?: unknown, homeScore?: unknown } | undefined} pick
 * @returns {boolean}
 */
export function isTiedScorePick(pick) {
  if (!pick) return false;
  const away = sanitizeScoreInput(pick.awayScore);
  const home = sanitizeScoreInput(pick.homeScore);
  return away !== '' && away === home;
}
